import { useState } from "react"
import CardFace from "@/components/CardFace/CardFace"
import CardPeek from "@/components/CardFace/CardPeek"
import CardThumb from "@/components/CardFace/CardThumb"
import EmptyState from "@/components/EmptyState/EmptyState"
import JobEditModal from "@/components/JobEditModal/JobEditModal"
import JobStatement from "@/components/JobStatement/JobStatement"
import Pill from "@/components/Pill/Pill"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import Spinner from "@/components/Spinner/Spinner"
import { DEEP_COPY, DEEP_DIVE_FIELDS } from "@/content/deepDive"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setDeepDive, setDeepDiveCard } from "@/services/jobs"
import { useSessionStore } from "@/store/sessionStore"
import { mustHaveCards, selectJob, selectSession } from "@/store/selectors"
import { EMPTY_DEEP_DIVE } from "@/types"
import type { CardId, DeepDiveDetail } from "@/types"
import styles from "@/styles/pages/Deep.module.css"

/*
 * Four boxes per keeper, in the room's words.
 *
 * The round exists to find out whether a Must have is actually a use case. Four
 * questions it has to survive — whose job, the pain today, the number that moves,
 * where it lives — and a card that cannot answer them is not ready, which is why
 * the screen says an empty box is a finding rather than nagging about it.
 *
 * The pool is the Must haves. If nothing was filed there the dive falls back to
 * the top of the shortlist rather than showing nothing, and says so: a
 * facilitator who skipped the board should still get the round.
 *
 * Everyone can type. This is the one round where that is right — the answers are
 * the room's own words, and `whoseJob` is usually answered by whoever holds that
 * job.
 */

const FALLBACK_POOL_SIZE = 3

interface DeepProps {
  canDrive: boolean
  /** The round is being read back: the four boxes are shown, never typed into. */
  readOnly: boolean
}

export default function Deep({ canDrive, readOnly }: DeepProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const [flipped, setFlipped] = useState(false)
  const [editing, setEditing] = useState(false)
  const { write, isPending } = useFirestoreWrite()

  if (!session || !job) return null

  const mustHaves = mustHaveCards(job)
  const fellBack = mustHaves.length === 0
  const pool = fellBack ? job.cardIds.slice(0, FALLBACK_POOL_SIZE) : mustHaves

  if (pool.length === 0) {
    return (
      <div className={styles.deep}>
        <RoundHeading title={DEEP_COPY.heading} objective={DEEP_COPY.objective} />
        <EmptyState title={DEEP_COPY.notTakenFurther} detail={DEEP_COPY.noMustHaves} />
      </div>
    )
  }

  /*
   * The card comes off the job, so every screen is on the one the facilitator
   * brought up. A stored id that is no longer in the pool — the card was dropped
   * from the shortlist, or demoted out of Must have — falls back to the first
   * rather than blanking the round.
   */
  const stored = job.deepDiveCardId
  const index = stored && pool.includes(stored) ? pool.indexOf(stored) : 0
  const cardId = pool[index]
  const answers = job.deepDive[cardId] ?? EMPTY_DEEP_DIVE
  const moving = pool.some((id) => isPending(id))

  /** How many of the four boxes this card has an answer in. */
  const filledFor = (id: CardId) => {
    const detail = job.deepDive[id]
    if (!detail) return 0
    return DEEP_DIVE_FIELDS.filter((field) => detail[field.key].trim()).length
  }

  return (
    <div className={styles.deep}>
      <RoundHeading
        title={DEEP_COPY.heading}
        objective={DEEP_COPY.objective}
        status={DEEP_COPY.position(index, pool.length)}
      />

      <JobStatement
        job={job}
        userStoryEnabled={session.config.userStoryEnabled}
        onEdit={canDrive && !readOnly ? () => setEditing(true) : undefined}
      />
      {editing ? (
        <JobEditModal
          sessionId={session.id}
          job={job}
          userStoryEnabled={session.config.userStoryEnabled}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {fellBack ? <Pill tone="warning">{DEEP_COPY.noMustHaves}</Pill> : null}

      <div className={styles.layout}>
        <div className={styles.card}>
          <CardFace
            cardId={cardId}
            side={flipped ? "back" : "front"}
            size="lg"
            onFlip={() => setFlipped((value) => !value)}
          />
        </div>

        {/*
         * Keyed by card id so each card's four debounced drafts belong to it.
         * Without that, switching cards mid-sentence would commit the half-typed
         * answer against whichever card the room moved to.
         */}
        <Boxes
          key={cardId}
          sessionId={session.id}
          jobId={job.id}
          cardId={cardId}
          answers={answers}
          readOnly={readOnly}
        />
      </div>

      {/*
       * The strip is how the room is moved between cards, and only the
       * facilitator moves it — everyone typing into a different card would make
       * the round impossible to run out loud.
       */}
      <div className={styles.strip}>
        <p className={styles.stripHint}>
          {canDrive ? DEEP_COPY.stripHintFacilitator : DEEP_COPY.stripHintRoom}
        </p>
        <div className={styles.tabs}>
          {/*
           * The peek wraps the whole tab rather than the thumb inside it: the
           * tab is a button, and the eye is one too. It also means a guest —
           * for whom the tab is disabled, because only the facilitator moves
           * the room — can still read the card the tab stands for.
           */}
          {pool.map((id, position) => (
            <CardPeek key={id} cardId={id}>
              <button
                type="button"
                className={position === index ? `${styles.tab} ${styles.tabOn}` : styles.tab}
                aria-current={position === index}
                /* One tab in flight locks the strip: this write moves every
                 * screen in the room, so two of them landing in an unknown
                 * order would put the room on a card neither press asked for.
                 *
                 * Locked for the same reason while the round is read back: the
                 * tab is a WRITE, and the whole room would follow it out of the
                 * beat they are actually in. So a review shows the card the room
                 * was left on, and the peek is still how the others are read. */
                disabled={!canDrive || moving || readOnly}
                aria-busy={isPending(id) || undefined}
                onClick={() => {
                  setFlipped(false)
                  write(() => setDeepDiveCard(session.id, job.id, id), id)
                }}
              >
                <CardThumb cardId={id} size="xs" />
                <span className={styles.progress}>
                  {isPending(id) ? (
                    <Spinner size="sm" />
                  ) : (
                    `${filledFor(id)}/${DEEP_DIVE_FIELDS.length}`
                  )}
                </span>
              </button>
            </CardPeek>
          ))}
        </div>
      </div>
    </div>
  )
}

function Boxes({
  sessionId,
  jobId,
  cardId,
  answers,
  readOnly,
}: {
  sessionId: string
  jobId: string
  cardId: CardId
  answers: DeepDiveDetail
  readOnly: boolean
}) {
  return (
    <div className={styles.boxes}>
      {DEEP_DIVE_FIELDS.map((field) => (
        <Box
          key={field.key}
          label={field.label}
          hint={field.hint}
          value={answers[field.key]}
          sessionId={sessionId}
          jobId={jobId}
          cardId={cardId}
          fieldKey={field.key}
          readOnly={readOnly}
        />
      ))}
      <p className={styles.finding}>{DEEP_COPY.emptyIsAFinding}</p>
    </div>
  )
}

function Box({
  label,
  hint,
  value,
  sessionId,
  jobId,
  cardId,
  fieldKey,
  readOnly,
}: {
  label: string
  hint: string
  value: string
  sessionId: string
  jobId: string
  cardId: CardId
  fieldKey: keyof DeepDiveDetail
  readOnly: boolean
}) {
  const { write } = useFirestoreWrite()
  const field = useDebouncedField({
    value,
    // One field per write, so two people answering two boxes at once both land.
    onCommit: (text) => write(() => setDeepDive(sessionId, jobId, cardId, { [fieldKey]: text })),
  })

  return (
    <label className={styles.box}>
      <span className={styles.boxLabel}>{label}</span>
      {/* `readOnly`, not `disabled`: an answer the room agreed on stays
          selectable and stays legible, which greyed-out text does not. */}
      <textarea
        className={styles.boxInput}
        placeholder={hint}
        readOnly={readOnly}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </label>
  )
}
